import { useI18n } from 'vue-i18n'

export interface CarrierOption {
  label: string
  value: string
}

export interface CarrierProgress {
  carrierCode: string
  carrierName: string
  logo: string
  status: 'waiting' | 'searching' | 'completed'
  count?: number
}

/**
 * 电商运价监控模块统一承运人选项
 * 所有涉及承运人下拉/过滤/展示的地方都应引用此文件
 */
export function useCarrierOptions() {
  const { t } = useI18n()

  // 可选择的承运人：PIL 已下线，HPL 拆分为 HPL_QQ / HPL_SPOT
  const carrierBaseList: Array<{ key: string; name: () => string; code: string }> = [
    { key: 'MSK', name: () => t('saas.rateQuery.carriers.maersk'), code: 'MSK' },
    { key: 'MSC', name: () => t('saas.rateQuery.carriers.msc'), code: 'MSC' },
    { key: 'CMA', name: () => t('saas.rateQuery.carriers.cmaCgm'), code: 'CMA' },
    { key: 'COSCO', name: () => t('saas.rateQuery.carriers.cosco'), code: 'COSCO' },
    { key: 'EMC', name: () => t('saas.rateQuery.carriers.evergreen'), code: 'EMC' },
    { key: 'HPL_QQ', name: () => t('saas.rateQuery.carriers.hplQq'), code: 'HPL_QQ' },
    { key: 'HPL_SPOT', name: () => t('saas.rateQuery.carriers.hplSpot'), code: 'HPL_SPOT' },
    { key: 'ONE', name: () => t('saas.rateQuery.carriers.one'), code: 'ONE' },
    { key: 'OOCL', name: () => t('saas.rateQuery.carriers.oocl'), code: 'OOCL' },
    { key: 'ZIM', name: () => t('saas.rateQuery.carriers.zim'), code: 'ZIM' },
    { key: 'YML', name: () => t('saas.rateQuery.carriers.yangMing'), code: 'YML' },
    { key: 'HMM', name: () => t('saas.rateQuery.carriers.hmm'), code: 'HMM' }
  ]

  /** 下拉选项：显示 "中文名 (CODE)"，方便用户识别 */
  const carrierOptions: CarrierOption[] = carrierBaseList.map(c => ({
    label: `${c.name()} (${c.code})`,
    value: c.key
  }))

  /** carrier code → 纯中文名映射，用于列表/图例等展示场景 */
  // 历史数据兼容：PIL 已下线、HPL 已拆分，但旧数据仍需要展示原名称
  const carrierLabelMap: Record<string, string> = {
    HPL: t('saas.rateQuery.carriers.hpl'),
    PIL: t('saas.rateQuery.carriers.pil')
  }
  for (const c of carrierBaseList) {
    carrierLabelMap[c.key] = c.name()
  }

  /** 获取承运人纯中文展示名，未命中则返回原始 code */
  const getCarrierLabel = (code: string): string => {
    if (!code) return ''
    return carrierLabelMap[code] || code
  }

  /** 构建搜索进度列表：传入已选承运人 code 数组，返回带 status/count 的进度项 */
  const buildCarrierProgressList = (selectedCarriers?: string[]): CarrierProgress[] => {
    const allItems: CarrierProgress[] = carrierBaseList.map(c => ({
      carrierCode: c.key,
      carrierName: c.name(),
      logo: '',
      status: 'waiting' as const,
      count: 0
    }))

    if (selectedCarriers && selectedCarriers.length > 0) {
      return selectedCarriers.map(code => {
        const existing = allItems.find(item => item.carrierCode === code)
        return existing
          ? { ...existing }
          : { carrierCode: code, carrierName: carrierLabelMap[code] || code, logo: '', status: 'searching' as const, count: 0 }
      })
    }
    return allItems
  }

  return { carrierOptions, carrierLabelMap, getCarrierLabel, buildCarrierProgressList }
}
